import { useEffect, useMemo, useState, type DragEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { leadsApi, ApiError } from "@/api";
import type { Lead, LeadStage } from "@/types";
import { LEAD_PIPELINE_STAGES, LEAD_OFFRAMP_STAGES } from "@/types";
import { Spinner } from "@/components/ui";
import { effectiveStage, stageLabel, STAGE_HINT, leadName, matchesQuery, isSet, isOpenLead } from "@/lib/leads";
import { LeadCard } from "./LeadCard";
import { LeadDrawer } from "./LeadDrawer";
import { AddLeadDrawer } from "./AddLeadDrawer";
import { usePush } from "@/staff/push/PushContext";
import "./leads.css";

type LeadsView = "all" | "open";

const VIEWS: { value: LeadsView; label: string }[] = [
  { value: "all", label: "All" },
  { value: "open", label: "Open only" },
];

// Survives board → drawer → away → back (the board unmounts on navigation) but
// resets in a fresh tab — same convention as the list view, except the board is
// a worklist so its fresh-tab default is "Open only", not "All".
const VIEW_STORAGE_KEY = "leads-board.view";

function storedView(): LeadsView {
  return sessionStorage.getItem(VIEW_STORAGE_KEY) === "all" ? "all" : "open";
}

export function LeadsBoard() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [q, setQ] = useState("");
  const [view, setView] = useState<LeadsView>(storedView);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [dragId, setDragId] = useState<number | null>(null);
  const [overStage, setOverStage] = useState<LeadStage | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Viewing the Pipeline is what "seeing" a consultation booking means:
  // stamp the viewer's watermark whenever unseen bookings exist while this
  // board is mounted (covers both arriving here and a booking landing while
  // already here), which clears the unseen part of the Leads nav badge.
  const { unseenBookings, markBookingsSeen } = usePush();
  useEffect(() => {
    if (unseenBookings > 0) void markBookingsSeen();
  }, [unseenBookings, markBookingsSeen]);

  // The header's "New lead" routes here with { create: true } — open the
  // Add-lead modal and strip the flag so a refresh/back doesn't re-open it.
  useEffect(() => {
    if ((location.state as { create?: boolean } | null)?.create) {
      setShowAdd(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  function selectView(v: LeadsView) {
    setView(v);
    sessionStorage.setItem(VIEW_STORAGE_KEY, v);
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    leadsApi
      .listLeads({ per_page: 200, include_archived: view === "all" })
      .then((res) => {
        if (!cancelled) setLeads(res.items ?? []);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load leads.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [view]);

  // The open view's fetch already omits archived (won) rows, but the server
  // still returns off-ramp stages — drop those too so the headline count
  // matches the cards on screen, same as the list view.
  const visible = useMemo(
    () =>
      (view === "open" ? leads.filter(isOpenLead) : leads).filter((l) => matchesQuery(l, q)),
    [leads, view, q],
  );

  // Quote provisioning archives a lead off the pipeline (its stage stays
  // wherever it was, usually "quoted"), so archived = closed-won. Bucket those
  // into their own Won column rather than their stale stage column.
  const won = useMemo(
    () =>
      visible
        .filter((l) => isSet(l.archived_at))
        .sort((a, b) => (b.archived_at || 0) - (a.archived_at || 0)),
    [visible],
  );

  const byStage = useMemo(() => {
    const map = {} as Record<LeadStage, Lead[]>;
    for (const s of [...LEAD_PIPELINE_STAGES, ...LEAD_OFFRAMP_STAGES]) map[s] = [];
    for (const l of visible) {
      if (isSet(l.archived_at)) continue; // rendered in the Won column instead
      const s = effectiveStage(l.stage); // legacy consulted → Qualified column
      (map[s] ?? (map[s] = [])).push(l);
    }
    map.scheduled.sort((a, b) => (a.consultation_at || 0) - (b.consultation_at || 0));
    return map;
  }, [visible]);

  function mergeLead(updated: Lead) {
    setLeads((ls) => ls.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
  }

  // New manual lead: drop the card into its column and open the detail drawer so
  // staff can immediately add the brief / record a consultation / create the quote.
  function handleLeadCreated(lead: Lead) {
    setShowAdd(false);
    if (!lead || lead.id == null) return; // shape guard — never render a card without an id
    setLeads((ls) => [lead, ...ls]);
    setSelectedId(lead.id);
  }

  async function changeStage(lead: Lead, stage: LeadStage, reason = "") {
    if (lead.stage === stage) return;
    const prev = lead;
    // optimistic
    setLeads((ls) =>
      ls.map((l) =>
        l.id === lead.id
          ? {
              ...l,
              stage,
              lost_at: stage === "lost" ? Date.now() : 0,
              ...(stage === "disqualified" ? { disqualified_reason: reason } : {}),
            }
          : l,
      ),
    );
    try {
      const updated = await leadsApi.moveStage(lead.id, stage, reason);
      mergeLead(updated);
    } catch (err) {
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? prev : l))); // rollback
      setError(err instanceof ApiError ? err.message : "Could not move the lead.");
    }
  }

  function onDropToStage(stage: LeadStage) {
    setOverStage(null);
    const lead = leads.find((l) => l.id === dragId);
    setDragId(null);
    if (!lead || lead.stage === stage) return;
    if (stage === "disqualified" || stage === "lost") {
      const reason = window.prompt(`Reason for marking "${leadName(lead)}" as ${stageLabel(stage)}? (optional)`, "");
      if (reason === null) return; // cancelled
      void changeStage(lead, stage, reason);
    } else {
      void changeStage(lead, stage);
    }
  }

  const selected = selectedId != null ? leads.find((l) => l.id === selectedId) ?? null : null;

  if (loading) {
    return (
      <div className="leads">
        <div className="leads__loading"><Spinner label="Loading leads…" /></div>
      </div>
    );
  }

  return (
    <div className="leads">
      <header className="leads__head">
        <div>
          <h1>Leads</h1>
          <p className="leads__count">{visible.length} shown</p>
        </div>
        <div className="leads__controls">
          <input
            className="leads__search"
            type="search"
            placeholder="Search name, email, company, postcode…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="leads__seg" role="tablist" aria-label="Open filter">
            {VIEWS.map((v) => (
              <button
                key={v.value}
                type="button"
                role="tab"
                aria-selected={view === v.value}
                className={`leads__seg-btn${view === v.value ? " is-on" : ""}`}
                onClick={() => selectView(v.value)}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      <div className="leads__board">
        {LEAD_PIPELINE_STAGES.map((stage) => (
          <Column
            key={stage}
            stage={stage}
            leads={byStage[stage]}
            over={overStage === stage}
            onOpen={setSelectedId}
            onDragStartCard={(id) => setDragId(id)}
            onDragEndCard={() => setDragId(null)}
            onDragOver={() => setOverStage(stage)}
            onDragLeave={() => setOverStage((s) => (s === stage ? null : s))}
            onDrop={() => onDropToStage(stage)}
          />
        ))}
      </div>

      {view === "all" ? (
        <>
          <h2 className="leads__closed-title">Closed</h2>
          <div className="leads__board leads__board--closed">
            <WonColumn leads={won} onOpen={setSelectedId} />
            {LEAD_OFFRAMP_STAGES.map((stage) => (
              <Column
                key={stage}
                stage={stage}
                leads={byStage[stage]}
                over={overStage === stage}
                onOpen={setSelectedId}
                onDragStartCard={(id) => setDragId(id)}
                onDragEndCard={() => setDragId(null)}
                onDragOver={() => setOverStage(stage)}
                onDragLeave={() => setOverStage((s) => (s === stage ? null : s))}
                onDrop={() => onDropToStage(stage)}
              />
            ))}
          </div>
        </>
      ) : null}

      {selected ? (
        <LeadDrawer
          lead={selected}
          onClose={() => setSelectedId(null)}
          onChanged={mergeLead}
        />
      ) : null}

      {showAdd ? (
        <AddLeadDrawer onCreated={handleLeadCreated} onClose={() => setShowAdd(false)} />
      ) : null}
    </div>
  );
}

/** Closed-won: leads archived off the pipeline by quote provisioning. Not a
 *  drop target, and its cards carry no drag handlers — a won lead lives on as
 *  its job, so there is nothing to move it back to. */
function WonColumn({ leads, onOpen }: { leads: Lead[]; onOpen: (id: number) => void }) {
  return (
    <section className="lead-col">
      <header className="lead-col__head">
        <span className="lead-col__title">Won</span>
        <span className="lead-col__count">{leads.length}</span>
      </header>
      <p className="lead-col__hint">quote accepted, now a job</p>
      <div className="lead-col__cards">
        {leads.map((lead) => (
          <LeadCard key={lead.id} lead={lead} onOpen={() => onOpen(lead.id)} />
        ))}
      </div>
    </section>
  );
}

function Column({
  stage,
  leads,
  over,
  onOpen,
  onDragStartCard,
  onDragEndCard,
  onDragOver,
  onDragLeave,
  onDrop,
}: {
  stage: LeadStage;
  leads: Lead[];
  over: boolean;
  onOpen: (id: number) => void;
  onDragStartCard: (id: number) => void;
  onDragEndCard: () => void;
  onDragOver: () => void;
  onDragLeave: () => void;
  onDrop: () => void;
}) {
  const hint = STAGE_HINT[stage];
  return (
    <section
      className={`lead-col${over ? " lead-col--over" : ""}`}
      onDragOver={(e: DragEvent) => {
        e.preventDefault();
        onDragOver();
      }}
      onDragLeave={onDragLeave}
      onDrop={(e: DragEvent) => {
        e.preventDefault();
        onDrop();
      }}
    >
      <header className="lead-col__head">
        <span className="lead-col__title">{stageLabel(stage)}</span>
        <span className="lead-col__count">{leads.length}</span>
      </header>
      {hint ? <p className="lead-col__hint">{hint}</p> : null}
      <div className="lead-col__cards">
        {leads.map((lead) => (
          <LeadCard
            key={lead.id}
            lead={lead}
            onOpen={() => onOpen(lead.id)}
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = "move";
              onDragStartCard(lead.id);
            }}
            onDragEnd={onDragEndCard}
          />
        ))}
      </div>
    </section>
  );
}
